// src/components/FICS/CompetitorAnalysis.js
import React from 'react';
import {
    Title,
    Text,
    Paper,
    Stack,
    Group,
    Badge,
    ThemeIcon,
    //Box, // Box was implicitly used by Text component="div", kept for clarity if needed elsewhere
    Tooltip,
    rem,
    Progress,
    Grid,
    useMantineTheme,
    useMantineColorScheme,
    //IconExternalLink,
    Anchor // <<< IMPORT Anchor for clickable URLs
} from '@mantine/core';
import { IconChartLine, IconQuestionMark, IconExternalLink } from '@tabler/icons-react';

const MAX_PROMINENCE = 10;
const MAX_STICKINESS = 10;

const getSimilarityAttributes = (distance) => {
    const similarity = 1 - distance;
    if (similarity > 0.7) return { color: 'red', label: 'High Similarity' };
    if (similarity > 0.5) return { color: 'orange', label: 'Medium Similarity' };
    if (similarity > 0.3) return { color: 'yellow', label: 'Low Similarity' };
    return { color: 'blue.3', label: 'Very Low Similarity' };
};

const ScoreDisplay = ({ label, score, color = 'blue', badgeSize = 'xs' }) => {
    //const theme = useMantineTheme(); // For N/A case, if we want specific color
    if (score === null || score === undefined) {
        // This text color will inherit from parent Group or can be set explicitly
        return <Text size="xs" c="dimmed">{label}: N/A</Text>;
    }
    return (
        <Group gap={rem(4)}>
            {/* This text color will inherit from parent Group */}
            <Text size="xs" fw={500}>{label}:</Text>
            <Badge color={color} size={badgeSize} variant="light">{score.toFixed(1)}</Badge>
        </Group>
    );
};

// NEW: Helper function for quartile labels (can be shared or duplicated from FICSPage if preferred)
// This is for the summary block within CompetitorAnalysis itself if you want it to be self-contained
// Or, you can pass prominencePotential and stickinessExpected as props from FICSPage.
// The provided FICSPage already calculates these. Let's assume they might be re-derived here
// for the summary section within this component for clarity, or you can adjust props.

// const deriveQuartileScoreLabelInternal = (score) => { // Renamed to avoid conflict if importing
// if (score === null || score === undefined || isNaN(parseFloat(score))) return "N/A";
// const numericScore = parseFloat(score);
// if (numericScore >= 7.5) return 'Very High';
// if (numericScore >= 5.0) return 'High';
// if (numericScore >= 2.5) return 'Moderate';
// if (numericScore >= 0) return 'Low';
// return "N/A";
// };

function CompetitorAnalysis({ data }) {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDarkGlobal = colorScheme === 'dark';

    // Fixed colors for the main "island"
    const primaryTextColorOnIsland = theme.black;
    const secondaryTextColorOnIsland = theme.colors.gray[7];
    const islandBgColor = theme.colors.gray[4];

    // Theme-aware colors for the main Tooltip
    const tooltipBgColorKey = isDarkGlobal ? 'dark.6' : 'gray.0';
    const tooltipTextColor = isDarkGlobal ? theme.white : theme.black;
    const tooltipGrapeColor = isDarkGlobal ? theme.colors.grape[4] : theme.colors.grape[7]; // Adjusted for readability
    const tooltipTealColor = isDarkGlobal ? theme.colors.teal[4] : theme.colors.teal[7];   // Adjusted for readability

    if (!data || !data.top_matches || data.top_matches.length === 0) {
        return (
            <Paper withBorder p="md" radius="md" mt="lg" bg={islandBgColor}>
                <Group>
                    <ThemeIcon color="gray" size="lg" radius="md">
                        <IconChartLine size="1.5rem" />
                    </ThemeIcon>
                    <div>
                        <Text fw={500} c={primaryTextColorOnIsland}>03 | Your Idea's "Value Potential"</Text>
                        <Text size="sm" c={secondaryTextColorOnIsland}>
                            Competitor analysis data is not available or no matches found.
                        </Text>
                    </div>
                </Group>
            </Paper>
        );
    }

    const { top_matches, average_prominence_score, average_stickiness_score, highest_prominence_score, highest_stickiness_score } = data;

    let prominencePotential = 'Unknown';
    let stickinessExpected = 'Unknown';

    if (highest_prominence_score !== null) {
        if (average_prominence_score >= 7.5) prominencePotential = 'Very High';
        else if (average_prominence_score >= 5) prominencePotential = 'High';
        else if (average_prominence_score >= 2.5) prominencePotential = 'Moderate';
        else prominencePotential = 'Low';
    }

    if (average_stickiness_score !== null) {
        if (average_stickiness_score >= 7.5) stickinessExpected = 'Very High';
        else if (average_stickiness_score >= 5) stickinessExpected = 'High';
        else if (average_stickiness_score >= 2.5) stickinessExpected = 'Moderate';
        else stickinessExpected = 'Low';
    }

    return (
        <Paper withBorder p="lg" radius="md" mt="xl" bg={islandBgColor}>
            <Title order={2} mb="lg" c={primaryTextColorOnIsland}>
                03 | Your Idea's "Value Potential"
            </Title>

            {/* "AI Assessment" Paper - fixed dark background, light text */}
            <Paper withBorder p="md" radius="sm" mb="lg" bg={theme.colors.gray[9]}>
                <Stack gap="xs">
                    <Text fw={700} size="md" c={theme.white}>
                        AI Assessment:
                    </Text>
                    <Text component="div" size="sm" c={theme.white} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Badge color="grape.3" variant="light" size="xl" mr={rem(4)}>
                            {prominencePotential}
                        </Badge>
                        <Text component="span" mr="xs">Prominence™ potential,</Text>
                        <Badge color="teal.3" variant="light" size="xl" mr={rem(4)} ml="xs">
                            {stickinessExpected}
                        </Badge>
                        <Text component="span">Stickiness™ expected.</Text>
                    </Text>
                    {/* Group for ScoreDisplay - text color set to light for dark background */}
                    <Group gap="md" mt="sm" c={theme.colors.dark[0]}>
                        <ScoreDisplay
                            label="Avg Prominence"
                            score={average_prominence_score}
                            color="grape.3"
                            badgeSize="xl"
                        />
                        <ScoreDisplay
                            label="Highest Prominence"
                            score={highest_prominence_score}
                            color="gray.2"
                            badgeSize="lg"
                        />
                        <ScoreDisplay
                            label="Avg Stickiness"
                            score={average_stickiness_score}
                            color="teal.3"
                            badgeSize="xl"
                        />
                        <ScoreDisplay
                            label="Highest Stickiness"
                            score={highest_stickiness_score}
                            color="gray.2"
                            badgeSize="lg"
                        />
                        <Tooltip
                            color={tooltipBgColorKey} // Set tooltip background color directly
                            label={
                                <>
                                    <Text c={tooltipGrapeColor} fw={700}>Prominence™ Score (out of 10):</Text>
                                    <Text c={tooltipTextColor} size="xs" mb="xs">
                                        Measures the visibility, relative to top-of-page postioning, of similar competitor articles. Higher score = greater potential exposure.
                                    </Text>
                                    <Text c={tooltipTealColor} fw={700} mt="sm">Stickiness™ Score (out of 10):</Text>
                                    <Text c={tooltipTextColor} size="xs">
                                        Reflects how durable similar competitor articles are on priority pages. Higher score = longer potential exposure.
                                    </Text>
                                </>
                            }
                            withArrow
                            multiline
                            width={220}
                            styles={(currentTheme) => ({ // Using currentTheme to avoid conflict with outer theme
                                tooltip: {
                                    border: `1px solid ${currentTheme.colorScheme === 'dark' ? currentTheme.colors.dark[4] : currentTheme.colors.gray[3]}`,
                                }
                            })}
                        >
                            <IconQuestionMark style={{ width: rem(30), height: rem(30) }} color={theme.colors.orange[5]} />
                        </Tooltip>
                    </Group>
                </Stack>
            </Paper>

            <Text size="md" c={secondaryTextColorOnIsland} mb="lg">
                Closest competitor matches based on semantic similarity and performance data:
            </Text>

            <Stack gap="md">
                {top_matches.map((match) => {
                    const similarityPercentage = Math.round((1 - match.distance) * 100);
                    return (
                        <Paper
                            key={match.comp_id}
                            withBorder
                            p="md"
                            radius="sm"
                            shadow="xs"
                            bg={theme.colors.dark[5]} // Fixed dark background for these item cards
                        >
                            <Group justify="space-between" align="flex-start">
                                <Stack gap="sm" style={{ flexGrow: 1 }}>
                                    <Group style={{ width: '100%' }} align="flex-start" wrap="nowrap" >
                                        <Stack gap={rem(2)} style={{ flexGrow: 1, marginRight: rem(8) }} >
                                            <Tooltip label={match.title}>
                                                <Text fw={500} lineClamp={4} c={theme.white}>
                                                    {match.title}
                                                </Text>
                                            </Tooltip>
                                            {/* MODIFICATION: Display Publisher and URL */}
                                            <Text size="xs" c={theme.colors.gray[5]}>
                                                {`Publisher: ${match.publisher || 'N/A'}`}
                                            </Text>
                                            {match.url && (
                                                <Anchor href={match.url} target="_blank" size="xs">
                                                    <Group gap={rem(4)} wrap="nowrap">
                                                        Original link
                                                        <IconExternalLink size={14} />
                                                    </Group>
                                                </Anchor>
                                            )}
                                            {/* END MODIFICATION */}

                                        </Stack>
                                        <Badge
                                            color={getSimilarityAttributes(match.distance).color}
                                            variant="light" // Will adapt to theme
                                            size="lg"
                                            style={{ flexShrink: 0 }}
                                        >
                                            {`Similarity: ${similarityPercentage}%`}
                                        </Badge>
                                    </Group>
                                    {(match.prominence_score !== null || match.stickiness_score !== null) && (
                                        <Grid gutter="md" mt="sm">
                                            {match.prominence_score !== null && (
                                                <Grid.Col span={6}>
                                                    <Text size="xs" c={theme.colors.gray[5]} mb={rem(2)}>Prominence™</Text>
                                                    <Tooltip
                                                        label={`Prominence: ${parseFloat(String(match.prominence_score)).toFixed(1)} / ${MAX_PROMINENCE}`}
                                                        withArrow
                                                    >
                                                        <Progress
                                                            value={(parseFloat(String(match.prominence_score)) / MAX_PROMINENCE) * 100}
                                                            color="grape"
                                                            size="lg"
                                                            radius="sm"
                                                            styles={(progressTheme) => ({
                                                                root: { backgroundColor: progressTheme.colorScheme === 'dark' ? progressTheme.colors.dark[4] : progressTheme.colors.gray[2] }
                                                            })}
                                                        />
                                                    </Tooltip>
                                                </Grid.Col>
                                            )}
                                            {match.stickiness_score !== null && (
                                                <Grid.Col span={match.prominence_score !== null ? 6 : 12}>
                                                    <Text size="xs" c={theme.colors.gray[5]} mb={rem(2)}>Stickiness™</Text>
                                                    <Tooltip
                                                        label={`Stickiness: ${parseFloat(String(match.stickiness_score)).toFixed(1)} / ${MAX_STICKINESS}`}
                                                        withArrow
                                                    >
                                                        <Progress
                                                            value={(parseFloat(String(match.stickiness_score)) / MAX_STICKINESS) * 100}
                                                            color="teal"
                                                            size="lg"
                                                            radius="sm"
                                                            styles={(progressTheme) => ({
                                                                root: { backgroundColor: progressTheme.colorScheme === 'dark' ? progressTheme.colors.dark[4] : progressTheme.colors.gray[2] }
                                                            })}
                                                        />
                                                    </Tooltip>
                                                </Grid.Col>
                                            )}
                                        </Grid>
                                    )}
                                </Stack>
                            </Group>
                        </Paper>
                    );
                })}
            </Stack>
        </Paper>
    );
}

export default CompetitorAnalysis;